import { SearchOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import {
  Checkbox,
  Divider,
  Input,
  List,
  Radio,
  RadioChangeEvent,
  Space
} from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { DefaultOptionType } from 'antd/lib/select';
import Button from 'app/components/Button';
import { differenceBy } from 'lodash';
import React from 'react';

const Container = styled(Space)`
  width: 100%;
  .select__wrapper {
    width: 100%;
  }
  .ant-divider {
    margin-top: 0px;
    margin-bottom: 8px;
  }
  .ant-list {
    max-height: 50vh;
    overflow: auto;
    .ant-list-item {
      border-bottom: 0;
    }
    .ant-list-footer {
      padding-bottom: 0;
    }
  }
`;

type Option = DefaultOptionType & Record<string, any>;

type Props = {
  type: 'checkbox' | 'radio';
  showSearch?: boolean;
  options: Array<Option>;
  value: Array<Option>;
  multiple?: boolean;
  onApply: (selected: Array<Option>) => void;
};

const SelectBox: React.FC<Props> = ({ options, value, onApply, type }) => {
  const [inputValue, setInputValue] = React.useState('');
  const [selections, setSelections] = React.useState([]);
  const Group = React.useMemo(() => {
    if (type === 'checkbox') return Checkbox.Group;
    return Radio.Group;
  }, [type]);

  const Menu = React.useMemo(() => {
    if (type === 'checkbox') return Checkbox;
    return Radio;
  }, [type]);

  const filteredOptions = React.useMemo(() => {
    if (inputValue) {
      return options.filter(({ label }) => {
        return label
          ?.toString()
          ?.toLowerCase()
          .includes(inputValue.toLowerCase());
      });
    }
    return options;
  }, [options, inputValue]);

  const defaultValue = React.useMemo(() => {
    return value.map((item) => {
      const { value: optionValue } = item;
      const selected = options.find((option) => option.value === optionValue);
      if (selected)
        return {
          ...selected
        };
      return item;
    });
  }, [value, options]);

  const shouldResetDisabled = React.useMemo(() => {
    const diff = differenceBy(selections, defaultValue, 'value');
    const removed = differenceBy(defaultValue, selections, 'value');
    if (removed.length) {
      return removed.length === 0;
    }
    return diff.length === 0;
  }, [defaultValue, selections]);

  const renderOption = (option: Option) => {
    return (
      <List.Item>
        <Menu value={option.value}>{option.label}</Menu>
      </List.Item>
    );
  };

  const onChangeSearch = (e) => {
    setInputValue(e.target.value);
  };
  const onCheckChange = (event: CheckboxValueType[] | RadioChangeEvent) => {
    if (Array.isArray(event)) {
      setSelections(
        event.map((checkedValue) => {
          const selected = options.find(
            (option) => option.value === checkedValue
          );
          if (selected)
            return {
              ...selected
            };
          return checkedValue;
        })
      );
    }
  };

  const handleApply = () => {
    onApply(selections);
  };

  const handleReset = () => {
    setSelections(defaultValue);
  };

  React.useEffect(() => {
    setSelections(defaultValue);
  }, [defaultValue]);

  return (
    <Container direction="vertical">
      <Input
        value={inputValue}
        onChange={onChangeSearch}
        prefix={<SearchOutlined />}
        placeholder="Search Inspectors"
      ></Input>
      <Group
        value={selections.map(({ value: selected }) => selected) as any}
        // defaultValue={defaultValue as any}
        className="select__wrapper"
        onChange={onCheckChange}
      >
        <List dataSource={filteredOptions} renderItem={renderOption}></List>
      </Group>
      <Divider />
      <Space>
        <Button type="primary" onClick={handleApply}>
          Apply
        </Button>
        <Button
          disabled={shouldResetDisabled}
          type="link"
          onClick={handleReset}
        >
          Reset
        </Button>
      </Space>
    </Container>
  );
};

export default SelectBox;
